import type { AccountRepresentativeListModel } from './account-representative.type.ts';
import type { LookupModel, SpecialtyModel } from './lookup.type.ts';
import type { RepresentativeStatusEnum } from './representative-status.type.ts';
import type { SearchParams } from './search-form.type.ts';
import type { SelectableUserListModel, UserListModel, UserModel } from './user-type.ts';

export interface RepresentativeModel extends UserModel {
  company: string;
  bio: string;
  customerId?: string;
  subscriptionId?: string;
  subscriptionStatus?: string;
  hasActiveSubscription?: boolean;
  subscriptionSource?: string;
  subscriptionProviderStatus?: string;
  subscriptionManagementSource?: string;
  subscriptionExpiresAt?: string;
  hasStoreSubscription?: boolean;
  hasWebSubscription?: boolean;
  canDelete?: boolean;
  cannotDeleteReasons?: string[];
  managerName: string;
  managerPhone: string;
  managerEmail: string;
  drugs: LookupModel[];
  specialties: RepresentativeSpecialtyModel[];
}

export interface RepresentativeSpecialtyModel extends SpecialtyModel, LookupModel {
  representativeId: number;
}

export interface RepresentativeListModel extends UserListModel {
  company: string;
  managerName: string;
  managerPhone: string;
  managerEmail: string;
  subscriptionStatus: string;
  hasActiveSubscription?: boolean;
  cellPhone: string;
  officePhone: string;
  extension: string;
  drugs: string[];
  specialties: string[];
}

export interface SelectableRepresentativeListModel
  extends RepresentativeListModel,
    SelectableUserListModel {}

export interface RepresentativeSearchModel {
  representative: RepresentativeListModel;
  accountRepresentatives: AccountRepresentativeListModel[];
}

export interface RepresentativeSearchParams extends SearchParams {
  accountKey?: string;
  representativeStatus?: RepresentativeStatusEnum;
  accountCategory?: number;
  activeOnly?: boolean;
  subscriptionStatus?: string;
}

export interface RepresentativeSubscriptionModel {
  provider: string;
  externalCustomerId?: string;
  externalSubscriptionId?: string;
  appUserId?: string;
  productId?: string;
  entitlementId?: string;
  status?: string;
  environment?: string;
  expiresAt?: string;
  billingIssueDetectedAt?: string;
  willRenew: boolean;
  lastSyncedAt?: string;
}

export interface RepresentativeAdminDetailsModel {
  id: number;
  key: string;
  active: boolean;
  createdAt: string;
  lastLoggedInAt?: string;
  agreeText: boolean;
  agreeEmail: boolean;
  receiveChats: boolean;
  hasActiveSubscription: boolean;
  subscriptionStatus?: string;
  subscriptionSource?: string;
  subscriptionProviderStatus?: string;
  subscriptionManagementSource?: string;
  subscriptionExpiresAt?: string;
  hasStoreSubscription: boolean;
  hasWebSubscription: boolean;
  customerId?: string;
  subscriptionId?: string;
  subscriptions: RepresentativeSubscriptionModel[];
  reporting: RepresentativeReportingModel;
}

export interface RepresentativeReportingModel {
  connectedAccounts: number;
  approvedAccounts: number;
  pendingAccounts: number;
  rejectedAccounts: number;
  favoriteAccounts: number;
  averageAccountRating?: number;
  totalMeetings: number;
  requestedMeetings: number;
  upcomingApprovedMeetings: number;
  completedMeetings: number;
  canceledMeetings: number;
  meetingsBookedFromSlots: number;
  openReservedSlots: number;
  mediaUploads: number;
}

export function representativeHasValidSubscription(
  user: { subscriptionStatus?: string; hasActiveSubscription?: boolean } | null | undefined,
): boolean {
  if (typeof user?.hasActiveSubscription === 'boolean') {
    return user.hasActiveSubscription;
  }

  return ['active', 'trialing'].includes(user?.subscriptionStatus);
}
